<template>
    <div class="max-w-full sm:max-w-md mx-auto inset-y-0 right-0 full-screen-height flex flex-col justify-center items-center">
        <div class="s-height shadow bg-white full-screen-height w-full relative sm:rounded-lg">
            <div class="swipeable-container" @touchstart="startSwipe" @touchend="endSwipe">
                <div class="screen-wrapper" ref="hello">
                    <div
                        v-if="currentScreen >= 2"
                        class="absolute flex items-center justify-between w-full px-8 gap-4 bottom-20 sm-h:bottom-10 yInReview-nav-btns"
                    >
                        <button
                            v-if="currentScreen === 14"
                            class="yInReview-nav-btn font-satoshi_m text-xl px-4 flex items-center gap-3 rounded-full"
                            @click="shareImage"
                            :disabled="loading"
                        >
                            <img src="../assets/images/year-in-review/share.svg" alt="share" class="w-7 h-7" />
                            Share
                        </button>
                        <div class="flex gap-5 ml-auto">
                            <button class="yInReview-nav-btn" @click="goBackward">
                                <img src="../assets/images/year-in-review/caret-left.svg" alt="previous" class="mx-auto" />
                            </button>
                            <button class="yInReview-nav-btn" @click="goForward">
                                <img src="../assets/images/year-in-review/caret-right.svg" alt="next" class="mx-auto" />
                            </button>
                        </div>
                    </div>
                    <component :is="activeComponent" :key="currentScreen" :reviewData="review" @registerProp="handleProp" @goForward="goForward" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { gsap } from "gsap";
import * as htmlToImage from "html-to-image";
import { saveAs } from "file-saver";

import login from "../components/yearInReview/login.vue";
import cover from "../components/yearInReview/cover.vue";
import deposit from "../components/yearInReview/Deposit.vue";
import accountDetails from "../components/yearInReview/AccountBalance.vue";
import conversions from "../components/yearInReview/Conversions.vue";
import transfers from "../components/yearInReview/Transfers.vue";
import loans from "../components/yearInReview/Loans.vue";
import premiumYield from "../components/yearInReview/PremiumYield.vue";
import enhancedDollar from "../components/yearInReview/EnhancedDollar.vue";
import commercialPaper from "../components/yearInReview/CommercialPaper.vue";
import targetSavings from "../components/yearInReview/TargetSavings.vue";
import billPayments from "../components/yearInReview/BillPayments.vue";
import walletInterest from "../components/yearInReview/WalletInterest.vue";
import cashback from "../components/yearInReview/Cashback.vue";
import recommendation from "../components/yearInReview/Recommendation.vue";

const GOOGLE_ANALYTICS_ID = "G-PQ6ZNRBF4X";

function loadGA(measurementId) {
    if (window.gtag) return;

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() {
        window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    gtag("js", new Date());
    gtag("config", measurementId);
}

export default {
    components: {
        login,
        cover,
        deposit,
        accountDetails,
        conversions,
        transfers,
        loans,
        premiumYield,
        enhancedDollar,
        commercialPaper,
        targetSavings,
        billPayments,
        walletInterest,
        cashback,
        recommendation,
    },

    data() {
        return {
            currentScreen: 0,
            review: {},
            touchStartY: 0,
            loading: false,

            animationOptions: ["scaleIn"],
            backAnimationOptions: ["scaleOut"],

            imageConfig: [
                "ArrowUpRight.svg",
                "balance-chart-low.svg",
                "balance-chart.svg",
                "balance-coin-low.svg",
                "balance-coin.svg",
                "bills-payment.svg",
                "blue-logo.svg",
                "caret-left.svg",
                "caret-right.svg",
                "caret-up.svg",
                "cashback-none.svg",
                "cashback.svg",
                "commercial-paper-none.svg",
                "commercial-paper.svg",
                "conversions-low.svg",
                "conversions-stamp.svg",
                "conversions.svg",
                "cover.svg",
                "deposit-over.svg",
                "deposit-under.svg",
                "deposit.svg",
                "enhanced-dollar-none.svg",
                "enhanced-dollar.svg",
                "loans-none.svg",
                "loans-rejected.svg",
                "loans.svg",
                "premium-yield-none.svg",
                "premium-yield.svg",
                "recommendation-1.svg",
                "recommendation-2.svg",
                "recommendation-3.svg",
                "recommendation-4.svg",
                "recommendation-5.svg",
                "recommendation-6.svg",
                "recommendation-7.svg",
                "share.svg",
                "target-savings-low.svg",
                "target-savings-none.svg",
                "target-savings.svg",
                "transfer.svg",
                "wallet-interest-none.svg",
                "wallet-interest.svg",
            ],
        };
    },

    computed: {
        activeComponent() {
            const map = {
                0: "login",
                1: "cover",
                2: "deposit",
                3: "accountDetails",
                4: "conversions",
                5: "transfers",
                6: "loans",
                7: "premiumYield",
                8: "enhancedDollar",
                9: "commercialPaper",
                10: "targetSavings",
                11: "billPayments",
                12: "walletInterest",
                13: "cashback",
                14: "recommendation",
            };
            return map[this.currentScreen];
        },
    },

    watch: {
        currentScreen(newVal, oldVal) {
            if (newVal > oldVal) {
                this.animateScreen();
            } else {
                this.animateScreen("backwards");
            }
        },
    },

    methods: {
        startSwipe(e) {
            if (this.currentScreen !== 1) return;
            this.touchStartY = e.touches[0].clientY;
        },

        endSwipe(e) {
            if (this.currentScreen !== 1) return;

            const dist = e.changedTouches[0].clientY - this.touchStartY;
            const min = 60;

            if (dist < -min) this.goForward();
            if (dist > min) this.goBackward();
        },

        getRandomAnimation(direction) {
            const length = this.animationOptions.length;

            const random = new Uint32Array(1);
            crypto.getRandomValues(random);

            const index = random[0] % length;

            if (direction === "backwards") return this.backAnimationOptions[index];
            return this.animationOptions[index];
        },

        animateScreen(direction = "forwards") {
            this.$nextTick(() => {
                const el = this.$refs.hello;
                const anim = this.getRandomAnimation(direction);

                const tl = gsap.timeline();

                switch (anim) {
                    case "scaleIn":
                        tl.fromTo(el, { scale: 0.9, opacity: 0.7 }, { scale: 1, opacity: 1, duration: 0.35 });
                        break;

                    case "scaleOut":
                        tl.fromTo(el, { scale: 1.3, opacity: 0.7 }, { scale: 1, opacity: 1, duration: 0.75 });
                        break;
                }
            });
        },

        handleProp(data) {
            this.review = data;
            this.currentScreen = 1;

            if (data?.customer_id && window.gtag) {
                window.gtag("config", GOOGLE_ANALYTICS_ID, {
                    user_id: data.customer_id,
                });
            }
        },

        goForward() {
            if (this.currentScreen < 14) {
                this.currentScreen++;
            }
        },

        goBackward() {
            if (this.currentScreen > 0) {
                this.currentScreen--;
            }
        },

        async generateBlob() {
            const element = this.$el;
            if (!element) throw new Error("Recommendation element not found");

            const blob = await htmlToImage.toBlob(element, {
                pixelRatio: 2,
                filter: (node) => {
                    const exclusionClasses = ["rec-btn", "yInReview-nav-btns"];
                    return !exclusionClasses.some((classname) => node.classList?.contains(classname));
                },
            });
            if (!blob) throw new Error("Failed to generate image");

            return blob;
        },

        async exportImage() {
            this.loading = true;
            try {
                const blob = await this.generateBlob();
                saveAs(blob, "recommendation.png");
            } catch (err) {
                console.error(err);
            } finally {
                this.loading = false;
            }
        },

        async shareImage() {
            this.loading = true;
            try {
                const blob = await this.generateBlob();
                const file = new File([blob], "recommendation.png", { type: "image/png" });

                if (!navigator.share) {
                    alert("Sharing not supported on this browser");
                    return;
                }

                if (!navigator.canShare({ files: [file] })) {
                    alert("File sharing not supported");
                    return;
                }

                window.gtag("event", "share_recap", {
                    event_label: "year_end_recap",
                    timestamp: new Date().toISOString(),
                });

                await navigator.share({
                    title: "Wow, what a year it's been! I just checked out my 2025 financial summary on Sycamore. It's pretty cool!",
                    text: "Check out my financial rank!",
                    files: [file],
                });
            } catch (err) {
                console.error(err);
            } finally {
                this.loading = false;
            }
        },
    },

    mounted() {
        loadGA(GOOGLE_ANALYTICS_ID);

        this.$preloadImages(this.imageConfig);
    },
};
</script>

<style scoped>
.screen-wrapper {
    width: 100%;
    height: 100%;
    position: absolute;
}

.yInReview-nav-btn {
    backdrop-filter: blur(26.513809204101562px);
    box-shadow: 6.63px 6.63px 1.1px -7.73px #ffffff80 inset;
    padding: 20px;
    background: #0000001a;
    border-radius: 50%;
    color: white;
}

.swipeable-container {
    width: 100%;
    height: 100vh;
    position: relative;
    cursor: pointer;
    overflow-y: hidden;
}

.priority-z-index {
    position: relative;
    z-index: 20;
}

.padded-page {
    padding-inline: 30px !important;
    padding-top: 100px !important;
}

.grayscale {
    filter: grayscale(100%);
}

@media (max-height: 750px) {
    .padded-page {
        padding-top: 60px !important;
    }
}

@supports (-webkit-touch-callout: none) {
    .swipeable-container {
        height: 100dvh;
    }
}
</style>
